import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid2,
  Typography,
  Button,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import {
  GoogleMap,
  useLoadScript,
  DirectionsRenderer,
  DirectionsService,
} from "@react-google-maps/api";
import RecentItems from "../RecentActivities/RecentActivitiesAndInsight";
import {
  GetBestRoute,
  GetCountByCategory,
} from "../../ApiServices/ApiRequests";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import {
  BestRoute,
  CategoryCount,
  Position,
  WayPoints,
} from "../../Models/Models";
import moderate from "../../Assests/SVG/moderate.svg";
import clean from "../../Assests/SVG/clean.svg";
import critical from "../../Assests/SVG/critical.svg";
import { showToast } from "../../CommonComponents/ToastBox";
import { googleMapsConfig } from "../../ApiServices/UrlConfigs";
import HeaderComponent from "../HeaderComponent/HeaderComponent";
import { useTranslation } from "react-i18next";
import { useMasterData } from "../../ContextAPI/MasterContext";

const GoogleMaps: React.FC = () => {
  type DirectionsResult = google.maps.DirectionsResult;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsConfig.apiKey,
  });
  const{masterState}=useMasterData();

  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();

  const { deviceLocations } = wasteManagementState;

  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value:
        | string
        | boolean
        | CategoryCount[]
        | number
        | { lat: number; lng: number }[]
        | null
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const initialMapCenter: Position = {
    lat: 1.3179623906210907,
    lng: 103.80952185215926,
  };

  const [directionsResponse, setDirectionsResponse] =
    useState<DirectionsResult | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  const calculateRoute = () => {
    if (deviceLocations.length > 0) {
      const directionsService: google.maps.DirectionsService =
        new google.maps.DirectionsService();

      // Dynamically create waypoints
      const waypoints: WayPoints[] = deviceLocations.map((location) => ({
        location: new google.maps.LatLng(location.lat, location.lng),
        stopover: true,
      }));

      directionsService.route(
        {
          origin: new google.maps.LatLng(13.026301469422833, 80.26556165454711),
          destination: new google.maps.LatLng(
            13.026301469422833,
            80.26556165454711
          ),
          waypoints: waypoints.length > 0 ? waypoints : undefined,
          optimizeWaypoints: true,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            setDirectionsResponse(result);

            // Generate the shareable link with waypoints for the route
            const origin = "13.026301469422833,80.26556165454711"; // Replace with the actual origin if dynamic
            const destination = "13.026301469422833,80.26556165454711"; // Replace with actual destination
            const waypointsParam: string = deviceLocations
              .map((loc) => `${loc.lat},${loc.lng}`)
              .join("|");

            setGeneratedLink(
              `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${encodeURIComponent(
                waypointsParam
              )}&travelmode=driving`
            );
          } else {
            console.error(`Error fetching directions: ${status}`, result);
          }
        }
      );
    } else {
      showToast("All Bins are Clean", "info");
    }
  };

  const fetchCount = async (siteCode: string): Promise<void> => {
    try {
      const data: CategoryCount[] = await GetCountByCategory(siteCode);

      // Find the corresponding values for clean, moderate, and critical from the API response
      const cleanData = data?.find(
        (item: CategoryCount) => item.name.toLowerCase() === "clean"
      );
      const moderateData = data?.find(
        (item: CategoryCount) => item.name.toLowerCase() === "moderate"
      );
      const criticalData = data?.find(
        (item: CategoryCount) => item.name.toLowerCase() === "critical"
      );

      // Set the values in context using handleInputChange, fallback to zero if not present
      handleInputChange("cleanCount", cleanData ? cleanData.value : "0");
      handleInputChange(
        "moderateCount",
        moderateData ? moderateData.value : "0"
      );
      handleInputChange(
        "criticalCount",
        criticalData ? criticalData.value : "0"
      );
    } catch (error) {
      console.error("Error while loading Plotting Bin data", error);
      handleInputChange("cleanCount", "0");
      handleInputChange("moderateCount", "0");
      handleInputChange("criticalCount", "0");
    }
  };

  useEffect(() => {
    if (masterState.selectedSiteCode.length > 0) {
      fetchCount(masterState.selectedSiteCode);
      fetchDirection(masterState.selectedSiteCode);
    }
  }, [masterState.selectedSiteCode]);

  const fetchDirection = async (siteCode: string): Promise<void> => {
    try {
      const response: BestRoute[] = await GetBestRoute(siteCode);
      const deviceLocations: Position[] = response.map((device) => ({
        lat: parseFloat(device.latitude),
        lng: parseFloat(device.longitude),
      }));

      handleInputChange("deviceLocations", deviceLocations);
    } catch (error) {
      console.error("Error fetching device data", error);
    }
  };

  const copyToClipboard = (): void => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      showToast("Link copied to clipboard!", "success");
    }
  };

  return (
    <Box sx={{ px: "10px", mt: 10.5 }}>
      <HeaderComponent />
      <Box>
        <Grid2 container spacing={4}>
          <Grid2 size={{ xs: 12, md: 8.5 }}>
            <Card variant="outlined" sx={{ p: 2, width: "100%", mt: "20px" }}>
              <Grid2 container spacing={2}>
                {/* Clean */}
                <Grid2 size={{ xs: 4 }}>
                  <Box display="flex" alignItems="center">
                    <img
                      src={clean}
                      alt="Clean Bin Icon"
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                      }}
                    />
                    <Typography variant="h5">
                      {t("global.Clean")}:{" "}
                      {wasteManagementState.cleanCount || 0}
                    </Typography>
                  </Box>
                </Grid2>

                {/* Moderate */}
                <Grid2 size={{ xs: 4 }}>
                  <Box display="flex" alignItems="center">
                    <img
                      src={moderate}
                      alt="Moderate Bin Icon"
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                      }}
                    />
                    <Typography variant="h5">
                      {t("global.Moderate")}:{" "}
                      {wasteManagementState.moderateCount || 0}
                    </Typography>
                  </Box>
                </Grid2>

                {/* Critical */}
                <Grid2 size={{ xs: 4 }}>
                  <Box display="flex" alignItems="center">
                    <img
                      src={critical}
                      alt="Critical Bin Icon"
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                      }}
                    />
                    <Typography variant="h5">
                      {t("global.Critical")}:{" "}
                      {wasteManagementState.criticalCount || 0}
                    </Typography>
                  </Box>
                </Grid2>
              </Grid2>
            </Card>

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={calculateRoute}>
                {t("global.FindBestRoute")}
              </Button>
            </Box>

            {/* Google Map */}
            <Box sx={{ mt: 2 }}>
              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "400px" }}
                  center={initialMapCenter}
                  zoom={12}
                  key={masterState.selectedSiteCode}
                >
                  {directionsResponse && (
                    <DirectionsRenderer directions={directionsResponse} />
                  )}
                </GoogleMap>
              )}
            </Box>

            {/* Shareable Link Section */}
            <Box sx={{ mt: 4 }}>
              {generatedLink && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                  <TextField
                    fullWidth
                    value={generatedLink}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <Tooltip title="Copy Link">
                    <IconButton onClick={copyToClipboard}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3.5 }}>
            <Box>
              <RecentItems />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default GoogleMaps;
